<template>
  <div class="offcanvas offcanvas-end" tabindex="-1" data-bs-backdrop="static" id="offCanvasNewExam" aria-labelledby="offCanvasNewExamLabel" ref="offcanvasElement">
    <div class="offcanvas-header">
      <button class="btn-back" id="closeBtn" type="button" name="button" data-test="btnCloseOffcanvas" aria-label="Close" @click="close">
        <IconArrowBack/>
        {{ $t('goBack') }}
      </button>
      <h5 class="offcanvas-title d-block" id="offCanvasNewExamLabel">{{ $t('exam.addExam') }}</h5>
    </div>
    <div class="offcanvas-body">
      <p>{{ $t('exam.addExamInfo') }}</p>

      <div class="patient-selected" v-if="patient.fullName">
        <p class="">{{ patient.fullName }}</p>
        <button type="button" class="no-btn" @click="patient = {}">
          <IconClose/>
        </button>
      </div>

      <button class="btn btn-dashed" type="button" name="button" @click="openOffcanvasAddPatient" v-else>{{ $t('exam.addPatient') }}</button>

      <div class="date-box">
        <div class="form-floating">
          <date-picker
            menuClassName="dp-date-time"
            v-model="exam.date"
            :placeholder="$t('exam.dateAndTime')"
            :locale="locale"
            value-type="date"
            :selectText="$t('createClinic.select')"
            :cancelText="$t('cancel')"
            minimumView="time"
            :format="$t('dateTimeFormatFns')"
            :inputFormat="$t('dateTimeFormatFns')"
            :min-date="new Date()"
            data-test="datePickerModalNewExam"/>
          <label for="examDate">{{ $t('exam.dateAndTime') }}</label>
        </div>
      </div>

      <!-- footer inside body to avoid empty space between body and footer -->
      <div class="offcanvas-footer">
        <button type="button"
                class="btn btn-success"
                :class="{'disabled': !patient.fullName}"
                data-test="btnSave"
                @click="doSave"
                :disabled="!patient.fullName || loading">
          {{ $t('exam.addExam') }}
        </button>
      </div>
    </div>

    <teleport to="body">
      <ExamPatientAddOffcanvas v-if="isPatientOpen" ref="sidenav" @offcanvas-patient="closePatient"/>
    </teleport>
  </div>
</template>

<style lang="scss" scoped>
#offCanvasNewExam {
  .offcanvas-title {
    color:                              var(--Brand-Primary, #1B294A);
    font-size:                          38px;
    font-weight:                        300;
    line-height:                        normal;
    margin:                             26px 0;
  }

  .offcanvas-body {
    display:                            flex;
    flex-direction:                     column;
    justify-content:                    flex-start !important;
    gap:                                26px;
    overflow-x:                         hidden;

    p:not(.patient-selected) {
      color:                            var(--Brand-Primary, #1B294A);
      font-size:                        var(--font-size-14);
      font-weight:                      300;
      line-height:                      140%; /* 19.6px */
      margin-bottom:                    0;
    }

    .patient-selected {
      @include                          border_radius(99px);
      display:                          flex;
      align-items:                      center;
      height:                           55px;
      margin-bottom:                    0;
      padding:                          20px;
      border:                           2px solid var(--Brand-Primary, #1B294A);
      background:                       var(--Shades-10, #FAFAFA);

      p {
        color:                          var(--Brand-Primary, #1B294A);
        text-align:                     center;
        font-size:                      18px;
        font-weight:                    700;
        line-height:                    normal;
        letter-spacing:                 0.9px;
        flex:                           1;
        justify-content:                center;
      }

      .no-btn svg * {
        stroke:                         var(--Brand-Primary, #1B294A);
      }
    }
  }

  .btn {
    font-size:                          var(--font-size-14);
    font-weight:                        700;
    line-height:                        normal;
    letter-spacing:                     4.2px;
    text-align:                         center;
    text-transform:                     uppercase;
    width:                              100%;
    margin:                             0;
    padding:                            20px;
  }

  .offcanvas-footer {
    display:                            flex;
    flex-direction:                     column;
    gap:                                26px;

    .btn-success {
      color:                            var(--Shades-W, #FFF);
    }
  }
}

.date-box {
  position:                             relative;
  width:                                100%;
  max-width:                            unset;
  margin:                               0;
}

@media (max-width: 991px) {
  #offCanvasNewExam {
    .btn:not(.btn-success) {
      font-size:                        var(--font-size-12);
    }
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'
import { enUS, ptBR, es } from 'date-fns/locale'
import ExamPatientAddOffcanvas from './ExamPatientAddOffcanvas.vue'
import { useExamFilterStore } from '@/store/examFilter';

export default {
  name: 'ExamNewOffcanvas',
  components: {
    ExamPatientAddOffcanvas
  },
  data: () => ({
    loading: false,
    alert: {
      message: null,
      class: null
    },
    exam: {
      date: new Date(),
      mydriasis: false
    },
    session: {
      collaborator: {},
      currentClinic: {}
    },
    offcanvas_instance: null,
    patient: {},
    isPatientOpen: false,
  }),
  computed: {
    locale() {
      return this.$utils.datePickerLocale()
    }
  },
  mounted() {
    const userStore = useUserStore()
    this.session = userStore.sessionInfo
  },
  methods: {
    show() {
      this.offcanvas_instance = bootstrap.Offcanvas.getOrCreateInstance(this.$refs.offcanvasElement)
      this.offcanvas_instance.show()
    },

    openOffcanvasAddPatient() {
      this.isPatientOpen = true

      this.$nextTick(() => {
        this.$refs.sidenav.openPatient()
      })
    },

    close() {
      if (this.offcanvas_instance)
        this.offcanvas_instance.hide()

      this.patient = {}
      this.$emit('close', 'canceled')
    },

    closePatient(patient) {
      this.isPatientOpen = false

      if (patient) {
        this.patient = patient
      }
    },

    async doSave() {
      await this.$helpers.requestHandling(async () => {
        const params = {
          id: this.patient.id,
          exam: this.exam
        }

        const response = await this.$patientService.saveExam(params)
        this.$message.popup(this, 'general.createdSuccess', 'operationSuccess', 'success')

        this.$nextTick(() => {
          const examFilterStore = useExamFilterStore()
          examFilterStore.reset()
          sessionStorage.removeItem('examFilters')

          this.$emit('close', 'saved')

          if (this.offcanvas_instance)
            this.offcanvas_instance.hide()

          this.patient = {}
        });
      }, this, 'general.createdError', 'operationFail');
    },
  },
  unmounted() {
    this.patient = {}
  }
}
</script>
